import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { GroupId } from "@custom-types/base-types";
import { BaseProjectProps } from "@custom-types/project-types";
import { isAllowedToChangeManagement } from "@pages/project-details/project-overview/change-management/change-management-utils";
import { useAppParams } from "@router/router-helper";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { HttpUtils } from "@stellar/utils";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { isProjectDemo } from "@utils/project-utils";
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

interface GroupMembersContextType extends BaseProjectProps {
  /** List of members belonging to the selected group */
  groupMembers: FaroCoreApiTypes.GroupMember[];

  /** Method to manipulate the selected group id */
  setSelectedGroupId: Dispatch<SetStateAction<GroupId | undefined>>;

  /** ID of the selected group whose members should be fetched */
  selectedGroupId?: GroupId;

  /** Whether the current user is allowed to modify project management */
  canChangeManagement: boolean;
}

// Context that provides the state of the group members
const GroupMembersContext = createContext<GroupMembersContextType | undefined>(
  undefined
);

/**
 * Provider that manages the state of the members of a specific group
 * based on the provided group ID.
 *
 * It automatically fetches the group members whenever the selected group ID changes,
 * and stores the data accordingly.
 */
export function GroupMembersProvider({
  project,
  children,
}: PropsWithChildren<BaseProjectProps>): JSX.Element {
  const { companyId } = useAppParams();
  const { handleErrorWithToast } = useErrorContext();

  const currentUser = useAppSelector(currentUserSelector);

  const [groupMembers, setGroupMembers] = useState<
    FaroCoreApiTypes.GroupMember[]
  >([]);
  const [selectedGroupId, setSelectedGroupId] = useState<GroupId | undefined>(
    project?.group?.id
  );

  useEffect(() => {
    async function onGroupSelected(
      groupId: GroupId | undefined
    ): Promise<void> {
      if (!companyId || !groupId) {
        return undefined;
      }

      setGroupMembers([]);

      try {
        const { members } = await faroCoreApiClient.groups.getGroupById({
          companyId,
          groupId,
        });
        setGroupMembers(members);
      } catch (error) {
        // Core API returns an error with status code 403 if the user does not have access to the resource
        const errorStatus = HttpUtils.getErrorStatus(error);
        if (
          errorStatus &&
          errorStatus === HttpUtils.HTTP_ERROR_CODE.Forbidden
        ) {
          setGroupMembers([]);
        } else {
          handleErrorWithToast({
            id: `getGroupById-${Date.now().toString()}`,
            title: "Cannot get the group details",
            error,
          });
        }
      }
    }

    const isRestrictedRole =
      currentUser?.role === "PROJECT_MANAGER" || currentUser?.role === "MEMBER";

    if (!isRestrictedRole) {
      void onGroupSelected(selectedGroupId);
    }
  }, [companyId, selectedGroupId, handleErrorWithToast, currentUser]);

  const canChangeManagement = useMemo(() => {
    // Prevent enabling management changes until all required data is loaded
    // Returns false by default while waiting for project, current user, or group members to be available
    if (!project || !currentUser || groupMembers.length === 0) {
      return false;
    }

    const isDemo = project && isProjectDemo(project);

    // If the project is a demo project, no one can change management
    // If the user is a company executive, they can change management
    if (!isDemo && currentUser.role === "COMPANY_EXECUTIVE") {
      return true;
    }

    const projectManager = project.managers.projectManager;

    if (!projectManager) {
      return false;
    }

    const currentUserMember = groupMembers.find(
      (member) => member.identity === currentUser.identity
    );

    // Project manager or group viewer can't access the change management
    // Only group manager can access the change management
    if (
      currentUserMember?.role === "PROJECT_MANAGER" ||
      currentUserMember?.role === "GROUP_VIEWER"
    ) {
      return false;
    }

    return isAllowedToChangeManagement(isDemo, project);
  }, [currentUser, groupMembers, project]);

  return (
    <GroupMembersContext.Provider
      value={{
        project,
        groupMembers,
        setSelectedGroupId,
        selectedGroupId,
        canChangeManagement,
      }}
    >
      {children}
    </GroupMembersContext.Provider>
  );
}

/**
 * Hook to consume the group members context.
 * Throws an error if used outside of the GroupMembersProvider context.
 */
export function useGroupMembersContext(): GroupMembersContextType {
  const context = useContext(GroupMembersContext);
  if (!context) {
    throw new Error(
      "useGroupMembersContext must be used within a GroupMembersProvider"
    );
  }

  return context;
}
